/**
 * Profile Page - Configuración de cuenta del usuario
 * Diseño tipo Canva/Notion con navegación lateral
 */

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useFirebase } from "../context/ApiContext";
import {
	Box,
	Typography,
	Avatar,
	Button,
	TextField,
	// Switch, Divider — usados en Preferencias (sección comentada)
	IconButton,
	Paper,
	Chip,
	LinearProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	InputAdornment,
	useMediaQuery,
	useTheme,
	Checkbox,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
// import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"; // Preferencias comentada
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
// NotificationsNoneOutlinedIcon, LanguageOutlinedIcon, DarkModeOutlinedIcon — Preferencias comentada
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockIcon from "@mui/icons-material/Lock";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";

import EmailVerificationModal from "../components/EmailVerificationModal";
import { LaunchCouponBanner } from "../components/LaunchCouponBanner";
import { LAUNCH_COUPON } from "../config/launchCoupon";

// Import styles
import "../components/Dashboard/Dashboard.css";

const menuItems = [
	{ id: "profile", label: "Mi perfil", icon: <PersonOutlineIcon /> },
	{ id: "plan", label: "Plan y facturación", icon: <DiamondOutlinedIcon /> },
	// Preferencias (comentado: no tenemos notificaciones, modo oscuro ni idioma implementados)
	// { id: "preferences", label: "Preferencias", icon: <SettingsOutlinedIcon /> },
	{ id: "security", label: "Seguridad", icon: <SecurityOutlinedIcon /> },
	{ id: "danger", label: "Zona de peligro", icon: <DeleteOutlineIcon /> },
];

const ProfilePage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { enqueueSnackbar } = useSnackbar();
	const {
		user,
		handleLogout,
		updateProfile,
		subscription,
		limits,
		changePassword,
		deleteAccount,
		purchaseSubscription,
		openCustomerPortal,
		isBlockedByEmailVerification,
		entitlements,
		purchasedGuides,
		getPacks,
	} = useFirebase();

	const isPro = limits?.isComplete === true;
	const currentGuides = limits?.currentGuides ?? 0;
	const maxGuides = limits?.maxGuides ?? 3;
	const guidesLabel = isPro ? "Ilimitadas" : `${currentGuides} / ${maxGuides}`;
	const progressPercent = isPro
		? 100
		: maxGuides > 0
		? Math.min(100, (currentGuides / maxGuides) * 100)
		: 0;
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	const [activeSection, setActiveSection] = useState(
		location.state?.section && menuItems.some((m) => m.id === location.state.section)
			? location.state.section
			: "profile",
	);
	const [isEditing, setIsEditing] = useState(false);

	// Abrir sección indicada por estado de navegación (ej. desde modal de límite de páginas)
	useEffect(() => {
		const section = location.state?.section;
		if (section && menuItems.some((m) => m.id === section)) {
			setActiveSection(section);
		}
	}, [location.state?.section]);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [showChangePasswordDialog, setShowChangePasswordDialog] =
		useState(false);
	const [planLoading, setPlanLoading] = useState(false);
	const [planAcceptNoWithdrawal, setPlanAcceptNoWithdrawal] = useState(false);
	const [planShowCheckError, setPlanShowCheckError] = useState(false);
	const [showEmailVerifyModal, setShowEmailVerifyModal] = useState(false);
	const [passwordForm, setPasswordForm] = useState({
		currentPassword: "",
		newPassword: "",
		repeatNewPassword: "",
	});
	const [passwordErrors, setPasswordErrors] = useState({});
	const [changingPassword, setChangingPassword] = useState(false);
	const [deletingAccount, setDeletingAccount] = useState(false);
	const [loadedPacks, setLoadedPacks] = useState([]);

	useEffect(() => {
		if (!getPacks) return;
		let cancelled = false;
		(async () => {
			try {
				const packs = await getPacks();
				if (!cancelled) setLoadedPacks(packs || []);
			} catch {
				/* no bloquea */
			}
		})();
		return () => { cancelled = true; };
	}, [getPacks]);

	const ownedPacks = useMemo(() => {
		const ids = entitlements?.ownedPackIds || [];
		return loadedPacks.filter((p) => ids.includes(p.id) || p.isOwned);
	}, [loadedPacks, entitlements?.ownedPackIds]);

	const individualGuides = useMemo(() => {
		const pgIds = purchasedGuides || [];
		const packGuideIds = new Set();
		for (const p of ownedPacks) {
			for (const gid of p.entitlements?.prebuiltGuides || []) packGuideIds.add(gid);
		}
		return pgIds.filter((gid) => !packGuideIds.has(gid));
	}, [purchasedGuides, ownedPacks]);

	// Form state - se sincroniza con user cuando cambia
	const [formData, setFormData] = useState({
		displayName: user?.displayName || user?.name || "",
		email: user?.email || "",
	});

	useEffect(() => {
		if (user) {
			setFormData((prev) => ({
				...prev,
				displayName: user.displayName || user.name || prev.displayName || "",
				email: user.email || prev.email,
			}));
		}
	}, [user?.id, user?.email, user?.displayName, user?.name]);

	// Preferences state (comentado: sección Preferencias desactivada)
	// const [preferences, setPreferences] = useState({
	// 	emailNotifications: true,
	// 	marketingEmails: false,
	// 	darkMode: false,
	// 	language: "es",
	// });

	const handleSaveProfile = async () => {
		try {
			await updateProfile({
				displayName: formData.displayName?.trim() || null,
			});
			setIsEditing(false);
			enqueueSnackbar("Perfil actualizado correctamente", {
				variant: "success",
			});
		} catch (err) {
			enqueueSnackbar(
				err?.response?.data?.message || "Error al guardar el perfil",
				{ variant: "error" }
			);
		}
	};

	const handleCloseChangePasswordDialog = () => {
		setShowChangePasswordDialog(false);
		setPasswordForm({
			currentPassword: "",
			newPassword: "",
			repeatNewPassword: "",
		});
		setPasswordErrors({});
	};

	const handleChangePasswordSubmit = async () => {
		const err = {};
		if (!passwordForm.currentPassword) err.currentPassword = "Requerido";
		if (!passwordForm.newPassword) err.newPassword = "Requerido";
		else if (passwordForm.newPassword.length < 6)
			err.newPassword = "Mínimo 6 caracteres";
		if (passwordForm.newPassword !== passwordForm.repeatNewPassword) {
			err.repeatNewPassword = "Las contraseñas no coinciden";
		}
		setPasswordErrors(err);
		if (Object.keys(err).length > 0) return;

		setChangingPassword(true);
		try {
			await changePassword(
				passwordForm.currentPassword,
				passwordForm.newPassword
			);
			enqueueSnackbar("Contraseña actualizada correctamente", {
				variant: "success",
			});
			handleCloseChangePasswordDialog();
		} catch (err) {
			const msg =
				err?.response?.data?.message ||
				err?.message ||
				"Error al cambiar la contraseña";
			enqueueSnackbar(msg, { variant: "error" });
		} finally {
			setChangingPassword(false);
		}
	};

	const handleExportData = () => {
		enqueueSnackbar(
			"Exportando datos... Te enviaremos un email cuando esté listo",
			{ variant: "info" }
		);
	};

	const handleDeleteAccount = async () => {
		setDeletingAccount(true);
		try {
			await deleteAccount();
			setShowDeleteDialog(false);
			enqueueSnackbar("Cuenta eliminada correctamente", { variant: "success" });
			handleLogout();
			navigate("/");
		} catch (err) {
			const msg = err?.response?.data?.error || err?.message || "Error al eliminar la cuenta";
			enqueueSnackbar(msg, { variant: "error" });
		} finally {
			setDeletingAccount(false);
		}
	};

	// Render sections
	const renderProfileSection = () => (
		<Box>
			<Typography
				variant="h5"
				sx={{ fontWeight: 700, color: "#1a1a2e", mb: 1 }}
			>
				Mi perfil
			</Typography>
			<Typography sx={{ color: "#64748B", mb: 4 }}>
				Gestiona tu información personal
			</Typography>

			{/* Avatar Section */}
			<Paper
				elevation={0}
				sx={{
					p: 3,
					borderRadius: "16px",
					border: "1px solid rgba(0, 0, 0, 0.06)",
					mb: 3,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
					<Box sx={{ position: "relative" }}>
						<Avatar
							sx={{
								width: 96,
								height: 96,
								background: "linear-gradient(135deg, #6366F1 0%, #A855F7 100%)",
								fontSize: "2.5rem",
								fontWeight: 700,
							}}
						>
							{formData.displayName?.charAt(0)?.toUpperCase() ||
								formData.email?.charAt(0)?.toUpperCase() ||
								"U"}
						</Avatar>
						<IconButton
							size="small"
							sx={{
								position: "absolute",
								bottom: 0,
								right: 0,
								backgroundColor: "#fff",
								border: "2px solid #fff",
								boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
								"&:hover": {
									backgroundColor: "#f8fafc",
								},
							}}
						>
							<CameraAltOutlinedIcon sx={{ fontSize: 18, color: "#64748B" }} />
						</IconButton>
					</Box>
					<Box>
						<Typography
							sx={{ fontWeight: 600, color: "#1a1a2e", fontSize: "1.125rem" }}
						>
							{formData.displayName || "Usuario"}
						</Typography>
						<Typography sx={{ color: "#64748B", fontSize: "0.875rem", mb: 1 }}>
							{formData.email}
						</Typography>
						<Chip
							icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
							label="Email verificado"
							size="small"
							sx={{
								backgroundColor: "rgba(16, 185, 129, 0.1)",
								color: "#059669",
								fontWeight: 600,
								fontSize: "0.75rem",
								"& .MuiChip-icon": {
									color: "#059669",
								},
							}}
						/>
					</Box>
				</Box>
			</Paper>

			{/* Personal Info */}
			<Paper
				elevation={0}
				sx={{
					p: 3,
					borderRadius: "16px",
					border: "1px solid rgba(0, 0, 0, 0.06)",
				}}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 3,
					}}
				>
					<Typography sx={{ fontWeight: 600, color: "#1a1a2e" }}>
						Información personal
					</Typography>
					<Button
						variant={isEditing ? "contained" : "outlined"}
						size="small"
						startIcon={isEditing ? <CheckCircleIcon /> : <EditOutlinedIcon />}
						onClick={() =>
							isEditing ? handleSaveProfile() : setIsEditing(true)
						}
						sx={{
							textTransform: "none",
							fontWeight: 600,
							borderRadius: "10px",
							...(isEditing
								? {
										background:
											"linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
								  }
								: {
										borderColor: "rgba(0, 0, 0, 0.12)",
										color: "#64748B",
								  }),
						}}
					>
						{isEditing ? "Guardar" : "Editar"}
					</Button>
				</Box>

				<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
					<Box>
						<Typography
							sx={{
								fontSize: "0.813rem",
								fontWeight: 600,
								color: "#64748B",
								mb: 1,
							}}
						>
							Nombre
						</Typography>
						{isEditing ? (
							<TextField
								fullWidth
								value={formData.displayName}
								onChange={(e) =>
									setFormData({ ...formData, displayName: e.target.value })
								}
								size="small"
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: "10px",
									},
								}}
							/>
						) : (
							<Typography sx={{ color: "#1a1a2e", fontWeight: 500 }}>
								{formData.displayName || "Sin nombre"}
							</Typography>
						)}
					</Box>

					<Box>
						<Typography
							sx={{
								fontSize: "0.813rem",
								fontWeight: 600,
								color: "#64748B",
								mb: 1,
							}}
						>
							Email
						</Typography>
						<Typography sx={{ color: "#1a1a2e", fontWeight: 500 }}>
							{formData.email}
						</Typography>
						<Typography sx={{ fontSize: "0.75rem", color: "#8B8FA3", mt: 0.5 }}>
							El email no se puede cambiar
						</Typography>
					</Box>

					<Box>
						<Typography
							sx={{
								fontSize: "0.813rem",
								fontWeight: 600,
								color: "#64748B",
								mb: 1,
							}}
						>
							Miembro desde
						</Typography>
						<Typography sx={{ color: "#1a1a2e", fontWeight: 500 }}>
							Enero 2026
						</Typography>
					</Box>
				</Box>
			</Paper>
		</Box>
	);

	const renderPlanSection = () => (
		<Box>
			<Typography
				variant="h5"
				sx={{ fontWeight: 700, color: "#1a1a2e", mb: 1 }}
			>
				Plan y facturación
			</Typography>
			<Typography sx={{ color: "#64748B", mb: 4 }}>
				Gestiona tu suscripción y métodos de pago
			</Typography>

			{/* Current Plan */}
			<Paper
				elevation={0}
				sx={{
					p: 3,
					borderRadius: "16px",
					border: "1px solid rgba(0, 0, 0, 0.06)",
					mb: 3,
				}}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						mb: 3,
					}}
				>
					<Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
							<Typography
								sx={{ fontWeight: 700, color: "#1a1a2e", fontSize: "1.25rem" }}
							>
								{isPro ? "Plan Pro" : "Plan Gratuito"}
							</Typography>
							<Chip
								label="Actual"
								size="small"
								sx={{
									backgroundColor: "rgba(99, 102, 241, 0.1)",
									color: "#6366F1",
									fontWeight: 600,
									fontSize: "0.688rem",
								}}
							/>
						</Box>
						<Typography sx={{ color: "#64748B", fontSize: "0.875rem" }}>
							{isPro
								? "Acceso completo a todas las guías premium, stickers y personalización ilimitada."
								: "Crea hasta 2 guías con plantillas básicas y stickers incluidos"}
						</Typography>
					</Box>
				</Box>

				{/* Usage */}
				<Box sx={{ mb: 3 }}>
					<Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
						<Typography
							sx={{ fontSize: "0.813rem", fontWeight: 600, color: "#64748B" }}
						>
							Guías creadas
						</Typography>
						<Typography
							sx={{ fontSize: "0.813rem", fontWeight: 600, color: "#1a1a2e" }}
						>
							{guidesLabel}
						</Typography>
					</Box>
					<LinearProgress
						variant="determinate"
						value={progressPercent}
						sx={{
							height: 8,
							borderRadius: 4,
							backgroundColor: "rgba(0, 0, 0, 0.06)",
							"& .MuiLinearProgress-bar": {
								borderRadius: 4,
								background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
							},
						}}
					/>
				</Box>

				{/* Features - mismos textos que en /pricing */}
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
					{(isPro
						? [
								{ text: "Crear guías ilimitadas", strong: true },
								{
									text: "Acceso a todas las guías premium de Viajes Molones",
									strong: true,
								},
								{
									text: "Nuevas guías incluidas automáticamente",
									strong: false,
								},
								{
									text: "Todas las colecciones de stickers premium",
									strong: true,
								},
								{
									text: "Personalización completa de tus guías",
									strong: false,
								},
								{
									text: "Compartir tus propias guías públicamente por enlace",
									strong: false,
								},
								{ text: "Vista privada ilimitada de tus guías", strong: false },
								{ text: "Pasaporte digital completo", strong: false },
						  ]
						: [
								{ text: "Crear hasta 2 guías", strong: false },
								{
									text: "Acceso a las guías gratuitas de Viajes Molones",
									strong: false,
								},
								{ text: "Usar plantillas básicas", strong: false },
								{
									text: "Añadir páginas, notas y contenido propio",
									strong: false,
								},
								{ text: "Stickers básicos incluidos", strong: false },
								{ text: "Vista privada de tus guías", strong: false },
								{ text: "Pasaporte digital completo", strong: false },
						  ]
					).map((feature, index) => (
						<Box
							key={index}
							sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
						>
							<CheckCircleIcon sx={{ fontSize: 18, color: "#10B981" }} />
							<Typography
								sx={{
									fontSize: "0.875rem",
									color: "#1a1a2e",
									fontWeight: feature.strong ? 600 : 400,
								}}
							>
								{feature.text}
							</Typography>
						</Box>
					))}
				</Box>

				{/* Tu plan actual - rejilla como en /pricing (solo si hay user y limits) */}
				{user && limits && (
					<Box
						sx={{
							mb: 3,
							p: 2.5,
							borderRadius: "16px",
							border: "1px solid rgba(0, 0, 0, 0.06)",
							backgroundColor: "rgba(0, 0, 0, 0.02)",
						}}
					>
						<Typography
							sx={{ fontWeight: 600, color: "#1a1a2e", fontSize: "1rem", mb: 2 }}
						>
							Tu plan actual
						</Typography>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
								gap: 2,
							}}
						>
							<Box>
								<Typography
									sx={{ fontSize: "0.813rem", color: "#64748B", fontWeight: 500 }}
								>
									Guías creadas
								</Typography>
								<Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "#1a1a2e" }}>
									{limits.currentGuides} /{" "}
									{limits.maxGuides === -1 ? "∞" : limits.maxGuides}
								</Typography>
							</Box>
							<Box>
								<Typography
									sx={{ fontSize: "0.813rem", color: "#64748B", fontWeight: 500 }}
								>
									Páginas por guía
								</Typography>
								<Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "#1a1a2e" }}>
									{limits.maxPagesPerGuide === -1
										? "∞"
										: limits.maxPagesPerGuide}
								</Typography>
							</Box>
							<Box>
								<Typography
									sx={{ fontSize: "0.813rem", color: "#64748B", fontWeight: 500 }}
								>
									Exportación
								</Typography>
								<Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "#1a1a2e" }}>
									{limits.canExport ? "✓ Habilitada" : "✗ No disponible"}
								</Typography>
							</Box>
							{subscription?.isComplete && subscription?.currentPeriodEnd && (
								<Box>
									<Typography
										sx={{ fontSize: "0.813rem", color: "#64748B", fontWeight: 500 }}
									>
										Renovación
									</Typography>
									<Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "#1a1a2e" }}>
										{new Date(
											subscription.currentPeriodEnd
										).toLocaleDateString("es-ES")}
									</Typography>
								</Box>
							)}
						</Box>
					</Box>
				)}
			</Paper>

			{/* Mis compras: packs y guías individuales */}
			{user && (ownedPacks.length > 0 || individualGuides.length > 0) && (
				<Paper
					elevation={0}
					sx={{
						p: 3,
						borderRadius: "16px",
						border: "1px solid rgba(0, 0, 0, 0.06)",
						mb: 3,
					}}
				>
					<Typography
						sx={{ fontWeight: 700, color: "#1a1a2e", fontSize: "1.1rem", mb: 2.5 }}
					>
						Mis compras
					</Typography>

					{ownedPacks.map((pack) => {
						const highlights = pack.marketing?.highlights || [];
						const extra = pack.entitlements?.extraPagesPerGuide || {};
						const extraLabel = Object.values(extra)
							.filter((v) => v > 0)
							.map((v) => `+${v} páginas extra`)
							.join(", ");
						return (
							<Box
								key={pack.id}
								sx={{
									p: 2,
									mb: individualGuides.length > 0 || ownedPacks.indexOf(pack) < ownedPacks.length - 1 ? 2 : 0,
									borderRadius: "12px",
									border: "1px solid rgba(99, 102, 241, 0.15)",
									backgroundColor: "rgba(99, 102, 241, 0.03)",
								}}
							>
								<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
									<Typography sx={{ fontWeight: 700, color: "#1a1a2e", fontSize: "0.938rem" }}>
										{pack.title}
									</Typography>
									<Chip
										label="Comprado"
										size="small"
										sx={{
											backgroundColor: "rgba(16, 185, 129, 0.1)",
											color: "#059669",
											fontWeight: 600,
											fontSize: "0.688rem",
										}}
									/>
								</Box>
								{pack.subtitle && (
									<Typography sx={{ color: "#64748B", fontSize: "0.813rem", mb: 1.5 }}>
										{pack.subtitle}
									</Typography>
								)}
								<Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
									{highlights.map((h, i) => (
										<Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
											<CheckCircleIcon sx={{ fontSize: 16, color: "#10B981", mt: "2px", flexShrink: 0 }} />
											<Typography sx={{ fontSize: "0.813rem", color: "#334155" }}>
												{h.title}
											</Typography>
										</Box>
									))}
									{extraLabel && !highlights.some((h) => h.title?.toLowerCase().includes("página")) && (
										<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
											<CheckCircleIcon sx={{ fontSize: 16, color: "#10B981", mt: "2px", flexShrink: 0 }} />
											<Typography sx={{ fontSize: "0.813rem", color: "#334155" }}>
												{extraLabel}
											</Typography>
										</Box>
									)}
								</Box>
							</Box>
						);
					})}

					{individualGuides.map((guideId) => (
						<Box
							key={guideId}
							sx={{
								p: 2,
								borderRadius: "12px",
								border: "1px solid rgba(0, 0, 0, 0.06)",
								backgroundColor: "rgba(0, 0, 0, 0.02)",
								mb: individualGuides.indexOf(guideId) < individualGuides.length - 1 ? 1.5 : 0,
							}}
						>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
								<Typography sx={{ fontWeight: 600, color: "#1a1a2e", fontSize: "0.875rem" }}>
									Guía: {guideId.charAt(0).toUpperCase() + guideId.slice(1)}
								</Typography>
								<Chip
									label="Comprada"
									size="small"
									sx={{
										backgroundColor: "rgba(16, 185, 129, 0.1)",
										color: "#059669",
										fontWeight: 600,
										fontSize: "0.688rem",
									}}
								/>
							</Box>
						</Box>
					))}
				</Paper>
			)}

			{/* Upgrade Card (solo si no es Pro) */}
			{!isPro && (
				<Paper
					elevation={0}
					sx={{
						p: 3,
						mb: 3,
						borderRadius: "16px",
						background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
						position: "relative",
						overflow: "hidden",
					}}
				>
					<Box
						sx={{
							position: "absolute",
							top: -50,
							right: -50,
							width: 200,
							height: 200,
							borderRadius: "50%",
							background:
								"radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 70%)",
						}}
					/>
					<Box sx={{ position: "relative", zIndex: 1 }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
							<RocketLaunchOutlinedIcon sx={{ color: "#A855F7" }} />
							<Typography
								sx={{ fontWeight: 700, color: "#fff", fontSize: "1.25rem" }}
							>
								Actualiza a Pro
							</Typography>
							<Chip
								label="50% OFF"
								size="small"
								sx={{
									background:
										"linear-gradient(135deg, #F59E0B 0%, #F97316 100%)",
									color: "#fff",
									fontWeight: 700,
									fontSize: "0.688rem",
								}}
							/>
						</Box>
						<Typography
							sx={{
								color: "rgba(255, 255, 255, 0.85)",
								mb: 2,
								fontSize: "0.875rem",
							}}
						>
							Con Pro tendrás:
						</Typography>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 1,
								mb: 3,
							}}
						>
							{[
								"Crear guías ilimitadas",
								"Acceso a todas las guías premium de Viajes Molones",
								"Nuevas guías incluidas automáticamente",
								"Todas las colecciones de stickers premium",
								"Personalización completa de tus guías",
								"Compartir tus guías públicamente por enlace",
								"Vista privada ilimitada",
								"Pasaporte digital completo",
							].map((text, index) => (
								<Box
									key={index}
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1.5,
									}}
								>
									<CheckCircleIcon
										sx={{ fontSize: 18, color: "rgba(16, 185, 129, 0.95)", flexShrink: 0 }}
									/>
									<Typography
										sx={{
											fontSize: "0.813rem",
											color: "rgba(255, 255, 255, 0.9)",
											fontWeight: 500,
										}}
									>
										{text}
									</Typography>
								</Box>
							))}
						</Box>
						<Box
							sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 2, flexWrap: "wrap" }}
						>
							{LAUNCH_COUPON.show ? (
								<>
									<Typography
										component="span"
										sx={{
											color: "#fff",
											fontWeight: 800,
											fontSize: "2rem",
											textDecoration: "line-through",
											opacity: 0.7,
											mr: 0.5,
										}}
									>
										{LAUNCH_COUPON.subscriptionPriceEur.toFixed(2).replace(".", ",")}€
									</Typography>
									<Typography
										component="span"
										sx={{ color: "rgba(255, 255, 255, 0.6)", fontSize: "0.875rem", textDecoration: "line-through", opacity: 0.7 }}
									>
										/año
									</Typography>
									<Typography
										sx={{
											color: "#fff",
											fontWeight: 800,
											fontSize: "2rem",
										}}
									>
										{LAUNCH_COUPON.priceAfterDiscount.toFixed(2).replace(".", ",")}€
									</Typography>
									<Typography
										sx={{ color: "rgba(255, 255, 255, 0.6)", fontSize: "0.875rem" }}
									>
										/año con cupón LANZAMIENTO
									</Typography>
								</>
							) : (
								<>
									<Typography
										sx={{
											color: "#fff",
											fontWeight: 800,
											fontSize: "2rem",
										}}
									>
										49€
									</Typography>
									<Typography
										sx={{ color: "rgba(255, 255, 255, 0.6)", fontSize: "0.875rem" }}
									>
										/año
									</Typography>
								</>
							)}
						</Box>
						{LAUNCH_COUPON.show && (
							<Box sx={{ mb: 1.5 }}>
								<LaunchCouponBanner
									compact
									sx={{
										bgcolor: "rgba(255, 255, 255, 0.15)",
										borderColor: "rgba(255, 255, 255, 0.4)",
										"& .MuiTypography-root": { color: "rgba(255, 255, 255, 0.95)" },
									}}
								/>
							</Box>
						)}
						<Box
							sx={{
								display: "flex",
								alignItems: "flex-start",
								gap: 0.75,
								mb: 1.5,
								...(planShowCheckError && {
									border: "2px solid #ef5350",
									borderRadius: "12px",
									bgcolor: "rgba(211, 47, 47, 0.15)",
									p: 0.75,
									animation: "consent-shake 0.5s ease-in-out",
								}),
							}}
						>
							<Checkbox
								size="small"
								checked={planAcceptNoWithdrawal}
								onChange={(e) => {
									setPlanAcceptNoWithdrawal(e.target.checked);
									setPlanShowCheckError(false);
								}}
								sx={{
									color: planShowCheckError ? "#ef5350" : "rgba(255, 255, 255, 0.6)",
									"&.Mui-checked": { color: "rgba(255, 255, 255, 0.95)" },
									mt: 0.15,
									p: 0.25,
								}}
							/>
							<Typography
								component="span"
								sx={{
									fontSize: "0.75rem",
									color: "rgba(255, 255, 255, 0.7)",
									lineHeight: 1.4,
								}}
							>
								Entiendo que al acceder al contenido digital pierdo el derecho
								de desistimiento de 14 días{" "}
								<Link
									to="/legal/terminos"
									style={{
										color: "rgba(255, 255, 255, 0.95)",
										textDecoration: "none",
									}}
									target="_blank"
									rel="nofollow"
								>
									(más info)
								</Link>
							</Typography>
						</Box>
						<Button
							variant="contained"
							fullWidth
							disabled={planLoading}
							onClick={async () => {
								if (isBlockedByEmailVerification?.("payment")) {
									setShowEmailVerifyModal(true);
									return;
								}
								if (!planAcceptNoWithdrawal) {
									setPlanShowCheckError(true);
									return;
								}
								setPlanLoading(true);
								try {
									await purchaseSubscription();
								} catch (err) {
									enqueueSnackbar(
										err?.message || "Error al iniciar el proceso de pago",
										{ variant: "error" }
									);
								} finally {
									setPlanLoading(false);
								}
							}}
							sx={{
								background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
								color: "#fff",
								textTransform: "none",
								fontWeight: 600,
								py: 1.5,
								borderRadius: "12px",
								fontSize: "0.938rem",
								"&:hover": {
									background:
										"linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
								},
							}}
							>
								{planLoading ? "Redirigiendo a Stripe..." : "Pagar y acceder ahora"}
							</Button>
					</Box>
				</Paper>
			)}

			{/* ¿También puedes comprar guías individuales? - como en /pricing */}
			<Paper
				elevation={0}
				sx={{
					p: 3,
					borderRadius: "16px",
					border: "1px solid rgba(0, 0, 0, 0.06)",
					mb: 3,
					backgroundColor: "rgba(248, 250, 252, 0.8)",
				}}
			>
				<Typography sx={{ fontWeight: 600, color: "#1a1a2e", fontSize: "1.1rem", mb: 1 }}>
					¿También puedes comprar guías individuales?
				</Typography>
				<Typography sx={{ color: "#64748B", fontSize: "0.875rem", mb: 2 }}>
					Si solo te interesa una guía específica, puedes comprarla de forma individual
					desde la página de explorar. Los precios varían según la guía.
				</Typography>
				<Button
					variant="outlined"
					size="small"
					onClick={() => navigate("/explorar")}
					sx={{
						textTransform: "none",
						fontWeight: 600,
						borderRadius: "10px",
						borderColor: "#6366F1",
						color: "#6366F1",
						"&:hover": { borderColor: "#4F46E5", backgroundColor: "rgba(99, 102, 241, 0.04)" },
					}}
				>
					Ver guías disponibles
				</Button>
			</Paper>

			{/* Si es Pro: enlace a gestionar suscripción */}
			{isPro && subscription?.currentPeriodEnd && (
				<Paper
					elevation={0}
					sx={{
						p: 3,
						borderRadius: "16px",
						border: "1px solid rgba(0, 0, 0, 0.06)",
					}}
				>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							flexWrap: "wrap",
							gap: 2,
						}}
					>
						<Box>
							<Typography
								sx={{ fontWeight: 600, color: "#1a1a2e", fontSize: "0.938rem" }}
							>
								Gestionar suscripción
							</Typography>
							<Typography
								sx={{ color: "#64748B", fontSize: "0.813rem", mt: 0.5 }}
							>
								Renovación:{" "}
								{new Date(subscription.currentPeriodEnd).toLocaleDateString(
									"es-ES",
									{ day: "numeric", month: "long", year: "numeric" }
								)}
								{subscription.cancelAtPeriodEnd
									? " (cancelada al final del periodo)"
									: ""}
							</Typography>
						</Box>
						<Button
							variant="outlined"
							size="small"
							disabled={planLoading}
							onClick={async () => {
								setPlanLoading(true);
								try {
									await openCustomerPortal();
								} catch (err) {
									enqueueSnackbar(
										err?.message || "Error al abrir el portal de gestión",
										{ variant: "error" }
									);
								} finally {
									setPlanLoading(false);
								}
							}}
							sx={{
								textTransform: "none",
								fontWeight: 600,
								borderRadius: "10px",
								borderColor: "#6366F1",
								color: "#6366F1",
							}}
						>
							{planLoading ? "Abriendo..." : "Gestionar suscripción"}
						</Button>
					</Box>
				</Paper>
			)}
		</Box>
	);

	// Preferencias: comentado porque no tenemos notificaciones, modo oscuro ni idioma implementados.
	// Para restaurar: descomentar el ítem en menuItems, el case "preferences" en renderContent y el cuerpo abajo.
	const renderPreferencesSection = () => null;
	/*
	const renderPreferencesSection = () => (
		<Box>
			<Typography variant="h5" sx={{ fontWeight: 700, color: "#1a1a2e", mb: 1 }}>Preferencias</Typography>
			<Typography sx={{ color: "#64748B", mb: 4 }}>Personaliza tu experiencia en la plataforma</Typography>
			<Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid rgba(0, 0, 0, 0.06)", mb: 3 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
					<NotificationsNoneOutlinedIcon sx={{ color: "#6366F1" }} />
					<Typography sx={{ fontWeight: 600, color: "#1a1a2e" }}>Notificaciones</Typography>
				</Box>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<Box>
							<Typography sx={{ fontWeight: 500, color: "#1a1a2e", fontSize: "0.938rem" }}>Notificaciones por email</Typography>
							<Typography sx={{ color: "#64748B", fontSize: "0.813rem" }}>Recibe actualizaciones sobre tus guías</Typography>
						</Box>
						<Switch checked={preferences.emailNotifications} onChange={(e) => setPreferences({ ...preferences, emailNotifications: e.target.checked })} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#6366F1" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#6366F1" } }} />
					</Box>
					<Divider />
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<Box>
							<Typography sx={{ fontWeight: 500, color: "#1a1a2e", fontSize: "0.938rem" }}>Emails de marketing</Typography>
							<Typography sx={{ color: "#64748B", fontSize: "0.813rem" }}>Novedades, ofertas y tips de viaje</Typography>
						</Box>
						<Switch checked={preferences.marketingEmails} onChange={(e) => setPreferences({ ...preferences, marketingEmails: e.target.checked })} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#6366F1" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#6366F1" } }} />
					</Box>
				</Box>
			</Paper>
			<Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid rgba(0, 0, 0, 0.06)", mb: 3 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
					<DarkModeOutlinedIcon sx={{ color: "#6366F1" }} />
					<Typography sx={{ fontWeight: 600, color: "#1a1a2e" }}>Apariencia</Typography>
				</Box>
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Box>
						<Typography sx={{ fontWeight: 500, color: "#1a1a2e", fontSize: "0.938rem" }}>Modo oscuro</Typography>
						<Typography sx={{ color: "#64748B", fontSize: "0.813rem" }}>Reduce el brillo de la interfaz</Typography>
					</Box>
					<Switch checked={preferences.darkMode} onChange={(e) => setPreferences({ ...preferences, darkMode: e.target.checked })} sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#6366F1" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#6366F1" } }} />
				</Box>
			</Paper>
			<Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: "1px solid rgba(0, 0, 0, 0.06)" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
					<LanguageOutlinedIcon sx={{ color: "#6366F1" }} />
					<Typography sx={{ fontWeight: 600, color: "#1a1a2e" }}>Idioma</Typography>
				</Box>
				<Box sx={{ display: "flex", gap: 1.5 }}>
					{[{ code: "es", label: "Español", flag: "🇪🇸" }, { code: "en", label: "English", flag: "🇬🇧" }].map((lang) => (
						<Box key={lang.code} onClick={() => setPreferences({ ...preferences, language: lang.code })} sx={{ px: 2.5, py: 1.5, borderRadius: "12px", border: preferences.language === lang.code ? "2px solid #6366F1" : "1px solid rgba(0, 0, 0, 0.08)", backgroundColor: preferences.language === lang.code ? "rgba(99, 102, 241, 0.05)" : "transparent", cursor: "pointer", transition: "all 0.15s ease", display: "flex", alignItems: "center", gap: 1, "&:hover": { borderColor: "#6366F1" } }}>
							<span style={{ fontSize: "1.25rem" }}>{lang.flag}</span>
							<Typography sx={{ fontWeight: 500, color: "#1a1a2e", fontSize: "0.875rem" }}>{lang.label}</Typography>
						</Box>
					))}
				</Box>
			</Paper>
		</Box>
	);
	*/

	const renderSecuritySection = () => (
		<Box>
			<Typography
				variant="h5"
				sx={{ fontWeight: 700, color: "#1a1a2e", mb: 1 }}
			>
				Seguridad
			</Typography>
			<Typography sx={{ color: "#64748B", mb: 4 }}>
				Protege tu cuenta y tus datos
			</Typography>

			{/* Password */}
			<Paper
				elevation={0}
				sx={{
					p: 3,
					borderRadius: "16px",
					border: "1px solid rgba(0, 0, 0, 0.06)",
					mb: 3,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
					<LockOutlinedIcon sx={{ color: "#6366F1" }} />
					<Typography sx={{ fontWeight: 600, color: "#1a1a2e" }}>
						Contraseña
					</Typography>
				</Box>

				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Box>
						<Typography
							sx={{
								fontWeight: 500,
								color: "#1a1a2e",
								fontSize: "0.938rem",
								marginBottom: 2,
							}}
						>
							Cambiar contraseña
						</Typography>
						<Typography sx={{ color: "#64748B", fontSize: "0.813rem" }}>
							Última actualización: Hace 3 meses
						</Typography>
					</Box>
					<Button
						variant="outlined"
						size="small"
						onClick={() => setShowChangePasswordDialog(true)}
						sx={{
							textTransform: "none",
							fontWeight: 600,
							borderRadius: "10px",
							borderColor: "rgba(0, 0, 0, 0.12)",
							color: "#64748B",
						}}
					>
						Cambiar
					</Button>
				</Box>
			</Paper>
		</Box>
	);

	const renderDangerSection = () => (
		<Box>
			<Typography
				variant="h5"
				sx={{ fontWeight: 700, color: "#1a1a2e", mb: 1 }}
			>
				Zona de peligro
			</Typography>
			<Typography sx={{ color: "#64748B", mb: 4 }}>
				Acciones irreversibles sobre tu cuenta
			</Typography>

			{/* Delete Account */}
			<Paper
				elevation={0}
				sx={{
					p: 3,
					borderRadius: "16px",
					border: "1px solid rgba(239, 68, 68, 0.3)",
					backgroundColor: "rgba(239, 68, 68, 0.02)",
				}}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						<Box
							sx={{
								width: 48,
								height: 48,
								borderRadius: "12px",
								backgroundColor: "rgba(239, 68, 68, 0.1)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<DeleteOutlineIcon sx={{ color: "#EF4444" }} />
						</Box>
						<Box>
							<Typography sx={{ fontWeight: 600, color: "#1a1a2e" }}>
								Eliminar cuenta
							</Typography>
							<Typography sx={{ color: "#64748B", fontSize: "0.813rem" }}>
								Elimina permanentemente tu cuenta y todos tus datos
							</Typography>
						</Box>
					</Box>
					<Button
						variant="outlined"
						onClick={() => setShowDeleteDialog(true)}
						sx={{
							textTransform: "none",
							fontWeight: 600,
							borderRadius: "10px",
							borderColor: "#EF4444",
							color: "#EF4444",
							"&:hover": {
								backgroundColor: "rgba(239, 68, 68, 0.08)",
								borderColor: "#EF4444",
							},
						}}
					>
						Eliminar
					</Button>
				</Box>
			</Paper>
		</Box>
	);

	const renderContent = () => {
		switch (activeSection) {
			case "profile":
				return renderProfileSection();
			case "plan":
				return renderPlanSection();
			case "preferences":
				return renderPreferencesSection(); // devuelve null; ítem oculto en menuItems
			case "security":
				return renderSecuritySection();
			case "danger":
				return renderDangerSection();
			default:
				return renderProfileSection();
		}
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				background: "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
			}}
		>
			{/* Header */}
			<Box
				sx={{
					backgroundColor: "rgba(255, 255, 255, 0.8)",
					backdropFilter: "saturate(180%) blur(20px)",
					borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
					position: "sticky",
					top: 0,
					zIndex: 100,
				}}
			>
				<Box
					sx={{
						maxWidth: 1200,
						mx: "auto",
						px: { xs: 2, md: 4 },
						py: 2,
						display: "flex",
						alignItems: "center",
						gap: 2,
					}}
				>
					<IconButton
						onClick={() => navigate("/")}
						sx={{
							backgroundColor: "rgba(0, 0, 0, 0.04)",
							"&:hover": {
								backgroundColor: "rgba(0, 0, 0, 0.08)",
							},
						}}
					>
						<ArrowBackIcon />
					</IconButton>
					<Typography
						sx={{ fontWeight: 600, color: "#1a1a2e", fontSize: "1.125rem" }}
					>
						Configuración de cuenta
					</Typography>
				</Box>
			</Box>

			{/* Content */}
			<Box
				sx={{
					maxWidth: 1200,
					mx: "auto",
					px: { xs: 2, md: 4 },
					py: 4,
					display: "flex",
					gap: 4,
					flexDirection: { xs: "column", md: "row" },
				}}
			>
				{/* Sidebar Navigation */}
				<Paper
					elevation={0}
					sx={{
						width: { xs: "100%", md: 280 },
						flexShrink: 0,
						borderRadius: "16px",
						border: "1px solid rgba(0, 0, 0, 0.06)",
						p: 1.5,
						height: "fit-content",
						position: { md: "sticky" },
						top: { md: 100 },
					}}
				>
					{menuItems.map((item) => (
						<Box
							key={item.id}
							onClick={() => setActiveSection(item.id)}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1.5,
								px: 2,
								py: 1.5,
								borderRadius: "12px",
								cursor: "pointer",
								backgroundColor:
									activeSection === item.id
										? "rgba(99, 102, 241, 0.1)"
										: "transparent",
								color: activeSection === item.id ? "#6366F1" : "#64748B",
								transition: "all 0.15s ease",
								mb: 0.5,
								"&:hover": {
									backgroundColor:
										activeSection === item.id
											? "rgba(99, 102, 241, 0.1)"
											: "rgba(0, 0, 0, 0.04)",
								},
								...(item.id === "danger" && {
									color: activeSection === item.id ? "#EF4444" : "#EF4444",
									backgroundColor:
										activeSection === item.id
											? "rgba(239, 68, 68, 0.1)"
											: "transparent",
									"&:hover": {
										backgroundColor: "rgba(239, 68, 68, 0.08)",
									},
								}),
							}}
						>
							{item.icon}
							<Typography
								sx={{
									fontWeight: activeSection === item.id ? 600 : 500,
									fontSize: "0.938rem",
								}}
							>
								{item.label}
							</Typography>
						</Box>
					))}
				</Paper>

				{/* Main Content */}
				<Box sx={{ flex: 1, minWidth: 0 }}>{renderContent()}</Box>
			</Box>

			{/* Change Password Dialog - mismo diseño que el login */}
			<Dialog
				open={showChangePasswordDialog}
				onClose={handleCloseChangePasswordDialog}
				PaperProps={{
					sx: {
						borderRadius: "16px",
						maxWidth: 440,
						p: 2.5,
						fontFamily:
							'"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
					},
				}}
			>
				<DialogTitle
					sx={{
						fontWeight: 700,
						fontSize: "1.25rem",
						letterSpacing: "-0.5px",
						background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
						backgroundClip: "text",
						WebkitBackgroundClip: "text",
						WebkitTextFillColor: "transparent",
						pt: 2,
						pb: 2,
					}}
				>
					Cambiar contraseña
				</DialogTitle>
				<DialogContent sx={{ pb: 0, pt: 3, overflow: "visible" }}>
					<TextField
						fullWidth
						label="Contraseña actual"
						type="password"
						value={passwordForm.currentPassword}
						onChange={(e) =>
							setPasswordForm((p) => ({
								...p,
								currentPassword: e.target.value,
							}))
						}
						error={!!passwordErrors.currentPassword}
						helperText={passwordErrors.currentPassword}
						placeholder="Tu contraseña actual"
						InputLabelProps={{
							sx: {
								color: "#64748B",
								fontWeight: 500,
								fontSize: "0.875rem",
								"&.Mui-focused": { color: "#6366F1" },
								"&.Mui-error": { color: "#DC2626" },
							},
						}}
						FormHelperTextProps={{ sx: { fontSize: "0.75rem", mt: 0.5 } }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<LockIcon sx={{ color: "text.secondary" }} />
								</InputAdornment>
							),
						}}
						sx={{
							mb: 2,
							"& .MuiOutlinedInput-root": {
								borderRadius: "12px",
								transition: "all 0.2s ease",
								"&:hover": {
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: "rgba(99, 102, 241, 0.5)",
									},
								},
								"&.Mui-focused": {
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: "#6366F1",
										borderWidth: "1.5px",
									},
								},
								"&.Mui-error": {
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: "rgba(220, 38, 38, 0.7)",
									},
								},
							},
						}}
					/>
					<TextField
						fullWidth
						label="Nueva contraseña"
						type="password"
						value={passwordForm.newPassword}
						onChange={(e) =>
							setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))
						}
						error={!!passwordErrors.newPassword}
						helperText={passwordErrors.newPassword}
						placeholder="Mínimo 6 caracteres"
						InputLabelProps={{
							sx: {
								color: "#64748B",
								fontWeight: 500,
								fontSize: "0.875rem",
								"&.Mui-focused": { color: "#6366F1" },
								"&.Mui-error": { color: "#DC2626" },
							},
						}}
						FormHelperTextProps={{ sx: { fontSize: "0.75rem", mt: 0.5 } }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<LockIcon sx={{ color: "text.secondary" }} />
								</InputAdornment>
							),
						}}
						sx={{
							mb: 2,
							"& .MuiOutlinedInput-root": {
								borderRadius: "12px",
								transition: "all 0.2s ease",
								"&:hover": {
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: "rgba(99, 102, 241, 0.5)",
									},
								},
								"&.Mui-focused": {
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: "#6366F1",
										borderWidth: "1.5px",
									},
								},
								"&.Mui-error": {
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: "rgba(220, 38, 38, 0.7)",
									},
								},
							},
						}}
					/>
					<TextField
						fullWidth
						label="Repetir nueva contraseña"
						type="password"
						value={passwordForm.repeatNewPassword}
						onChange={(e) =>
							setPasswordForm((p) => ({
								...p,
								repeatNewPassword: e.target.value,
							}))
						}
						error={!!passwordErrors.repeatNewPassword}
						helperText={passwordErrors.repeatNewPassword}
						placeholder="Confirma la nueva contraseña"
						InputLabelProps={{
							sx: {
								color: "#64748B",
								fontWeight: 500,
								fontSize: "0.875rem",
								"&.Mui-focused": { color: "#6366F1" },
								"&.Mui-error": { color: "#DC2626" },
							},
						}}
						FormHelperTextProps={{ sx: { fontSize: "0.75rem", mt: 0.5 } }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<LockIcon sx={{ color: "text.secondary" }} />
								</InputAdornment>
							),
						}}
						sx={{
							"& .MuiOutlinedInput-root": {
								borderRadius: "12px",
								transition: "all 0.2s ease",
								"&:hover": {
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: "rgba(99, 102, 241, 0.5)",
									},
								},
								"&.Mui-focused": {
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: "#6366F1",
										borderWidth: "1.5px",
									},
								},
								"&.Mui-error": {
									"& .MuiOutlinedInput-notchedOutline": {
										borderColor: "rgba(220, 38, 38, 0.7)",
									},
								},
							},
						}}
					/>
				</DialogContent>
				<DialogActions
					sx={{ justifyContent: "flex-end", px: 2.5, pb: 2.5, pt: 2, gap: 1.5 }}
				>
					<Button
						variant="outlined"
						onClick={handleCloseChangePasswordDialog}
						disabled={changingPassword}
						sx={{
							textTransform: "none",
							fontWeight: 600,
							fontSize: "0.875rem",
							borderRadius: "12px",
							py: 1,
							px: 2.5,
							borderColor: "rgba(0, 0, 0, 0.12)",
							color: "#64748B",
							"&:hover": {
								borderColor: "rgba(99, 102, 241, 0.5)",
								backgroundColor: "rgba(99, 102, 241, 0.04)",
							},
						}}
					>
						Cancelar
					</Button>
					<Button
						variant="contained"
						onClick={handleChangePasswordSubmit}
						disabled={changingPassword}
						sx={{
							textTransform: "none",
							fontWeight: 600,
							fontSize: "0.875rem",
							borderRadius: "12px",
							py: 1,
							px: 2.5,
							backgroundColor: "#6366F1 !important",
							color: "#fff !important",
							boxShadow: "0 2px 8px rgba(99, 102, 241, 0.3)",
							transition: "all 0.2s ease",
							"&:hover": {
								backgroundColor: "#4F46E5 !important",
								boxShadow: "0 4px 12px rgba(99, 102, 241, 0.4)",
							},
							"&:disabled": {
								background: "rgba(0, 0, 0, 0.12) !important",
								color: "rgba(0, 0, 0, 0.26) !important",
							},
						}}
					>
						{changingPassword ? (
							<CircularProgress size={24} sx={{ color: "#fff" }} />
						) : (
							"Guardar"
						)}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete Confirmation Dialog - a nivel raíz para que siempre esté montado */}
			<Dialog
				open={showDeleteDialog}
				onClose={() => setShowDeleteDialog(false)}
				PaperProps={{
					sx: {
						borderRadius: "20px",
						maxWidth: 440,
						p: 1,
					},
				}}
			>
				<DialogTitle sx={{ textAlign: "center", pt: 4 }}>
					<Box
						sx={{
							width: 64,
							height: 64,
							borderRadius: "16px",
							backgroundColor: "rgba(239, 68, 68, 0.1)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							mx: "auto",
							mb: 2,
						}}
					>
						<WarningAmberIcon sx={{ fontSize: 32, color: "#EF4444" }} />
					</Box>
					<Typography
						sx={{ fontWeight: 700, color: "#1a1a2e", fontSize: "1.25rem" }}
					>
						¿Eliminar tu cuenta?
					</Typography>
				</DialogTitle>
				<DialogContent sx={{ textAlign: "center", pb: 2 }}>
					<Typography sx={{ color: "#64748B" }}>
						Esta acción es permanente y no se puede deshacer. Se eliminarán
						todas tus guías, configuraciones y datos personales.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ justifyContent: "center", pb: 3, gap: 1.5 }}>
					<Button
						variant="outlined"
						onClick={() => setShowDeleteDialog(false)}
						disabled={deletingAccount}
						sx={{
							textTransform: "none",
							fontWeight: 600,
							borderRadius: "12px",
							px: 4,
							borderColor: "rgba(0, 0, 0, 0.12)",
							color: "#64748B",
						}}
					>
						Cancelar
					</Button>
					<Button
						variant="contained"
						onClick={handleDeleteAccount}
						disabled={deletingAccount}
						sx={{
							textTransform: "none",
							fontWeight: 600,
							borderRadius: "12px",
							px: 4,
							backgroundColor: "#EF4444",
							"&:hover": {
								backgroundColor: "#DC2626",
							},
						}}
					>
						{deletingAccount ? (
							<CircularProgress size={24} sx={{ color: "#fff" }} />
						) : (
							"Sí, eliminar cuenta"
						)}
					</Button>
				</DialogActions>
			</Dialog>

			<EmailVerificationModal
				open={showEmailVerifyModal}
				onClose={() => setShowEmailVerifyModal(false)}
			/>
		</Box>
	);
};

export default ProfilePage;
